import { ChevronLeft, ChevronRight } from 'lucide-react'
import type React from 'react'
import { useEffect } from 'react'
import styled from 'styled-components'
import { useDiagnostics } from '../../hooks/useDiagnostics'
import { Card } from '../common/Card'
import { NavButton } from '../common/NavButton'
import { VO2Chart } from './VO2Chart'

const PageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`

const ButtonContainer = styled.div`
display: flex;
align-items: center;
gap: ${({ theme }) => theme.spacing.md};
`

const ChartSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.md};
`

const StatCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.sm};
  text-align: center;
`

const StatValueLg = styled.div`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const LatestTestDate = styled.p`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`

interface DiagnosticsPageProps {
  athleteId: string
}

export const DiagnosticsPage: React.FC<DiagnosticsPageProps> = ({ athleteId }) => {
  const { data: diagnosticsData, diagnosticStats, loading, loadDiagnosticsData } = useDiagnostics(athleteId)

  useEffect(() => {
    loadDiagnosticsData()
  }, [loadDiagnosticsData])

  return (
    <PageContainer>
      {/*<DiagnosticsUploadPage athleteId={athleteId}></DiagnosticsUploadPage>*/}

      {loading ? (
        <LoadingMessage>Loading diagnostics data...</LoadingMessage>
      ) : (
        <>
          {diagnosticStats && (
            <>
              <ButtonContainer>
                <NavButton disabled={true} title="Previous test">
                  <ChevronLeft size={16} />
                </NavButton>
                <LatestTestDate> Latest Test: {diagnosticStats.latestTest}</LatestTestDate>
                <NavButton disabled={true} title="Next test">
                  <ChevronRight size={16} />
                </NavButton>
              </ButtonContainer>

              <DataGrid>
                <StatCard>
                  <StatLabel>VT1</StatLabel>
                  <StatValueLg>{diagnosticStats.vt1?.pace_min_per_km?.substring(4)} min/km</StatValueLg>
                </StatCard>
                <StatCard>
                  <StatLabel>VT2</StatLabel>
                  <StatValueLg>{diagnosticStats.vt2?.pace_min_per_km?.substring(4)} min/km</StatValueLg>
                </StatCard>
                <StatCard>
                  <StatLabel>HR max</StatLabel>
                  <StatValueLg>{diagnosticStats.maxHR}</StatValueLg>
                </StatCard>
                <StatCard>
                  <StatLabel>
                    VO2<sub>max</sub> (mL/min/kg)
                  </StatLabel>
                  <StatValueLg>{diagnosticStats.VO2max}</StatValueLg>
                </StatCard>
                <StatCard>
                  <StatLabel>Weight (kg)</StatLabel>
                  <StatValueLg>{diagnosticStats.weight}</StatValueLg>
                </StatCard>
              </DataGrid>
            </>
          )}

          {diagnosticsData.length > 0 ? (
            <ChartSection>
              <VO2Chart data={diagnosticsData} />
            </ChartSection>
          ) : (
            !loading && (
              <LoadingMessage>No diagnostics data available. Upload an XLSX file to get started.</LoadingMessage>
            )
          )}
        </>
      )}
    </PageContainer>
  )
}
